import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Database, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { exportQuoteCalculatorPdf, exportQuoteCalculatorExcel } from "../lib/exportQuoteCalculator";
import { formatCurrency } from "../lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function computeGroups(rows) {
  const groupSize = new Array(rows.length).fill(0);
  let i = 0;
  while (i < rows.length) {
    let j = i;
    while (j < rows.length && rows[j].product_id === rows[i].product_id) j++;
    groupSize[i] = j - i;
    i = j;
  }
  return groupSize;
}

export default function QuoteCalculatorPage() {
  const { token } = useParams();
  const [cartName, setCartName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [items, setItems] = useState([]);
  const [cartonQty, setCartonQty] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/quote/${token}`)
      .then(({ data }) => {
        setCartName(data.cart_name);
        setCurrency(data.currency || "USD");
        setItems(data.items);
        const initial = {};
        data.items.forEach((i) => { initial[i.item_id] = i.carton_qty || 0; });
        setCartonQty(initial);
      })
      .catch((e) => setError(e.response?.data?.detail || "This quote link does not exist or has expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const computedItems = useMemo(() => items.map((i) => {
    const carton_qty = cartonQty[i.item_id] || 0;
    const total_qty = carton_qty * (i.pcs_per_ctn || 0);
    const total_amount = total_qty * (i.final_price ?? i.price ?? 0);
    return { ...i, carton_qty, total_qty, total_amount };
  }), [items, cartonQty]);

  const groupSize = computeGroups(computedItems);
  const totalCbm = computedItems.reduce((s, i) => s + (i.cbm || 0) * i.carton_qty, 0);
  const grandTotal = computedItems.reduce((s, i) => s + i.total_amount, 0);

  const handleExportPdf = async () => {
    toast.info("Generating PDF...");
    try {
      await exportQuoteCalculatorPdf(computedItems, cartName, currency);
      toast.success("PDF exported successfully");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleExportExcel = async () => {
    toast.info("Generating Excel...");
    try {
      await exportQuoteCalculatorExcel(computedItems, cartName, currency);
      toast.success("Excel exported successfully");
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-gray-400" data-testid="quote-loading">Loading...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="quote-error">
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-6 sm:px-10 py-8" data-testid="quote-calculator-page">
      <div className="max-w-screen-xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center">
              <Database size={18} className="text-brand-600" strokeWidth={1.75} />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-gray-900">Quote Calculator</h1>
              <p className="text-sm text-gray-400" data-testid="quote-customer-name">Customer: {cartName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExportPdf} data-testid="quote-export-pdf-button" className="rounded-lg border-gray-300 text-xs h-9">
              <FileText size={13} className="mr-1.5" /> Export PDF
            </Button>
            <Button onClick={handleExportExcel} data-testid="quote-export-excel-button" className="rounded-lg bg-brand-600 hover:bg-brand-700 text-xs h-9">
              <FileSpreadsheet size={13} className="mr-1.5" /> Export Excel
            </Button>
          </div>
        </div>

        <p className="text-sm text-gray-500 border-t border-b border-gray-100 py-4 leading-relaxed">
          Fill in a carton quantity for any product below - the total amount and CBM are calculated automatically in your browser.
          Nothing you enter here is saved back to Better Home Fashion Inc's system (only the PDF/Excel file you explicitly export is
          generated for you to download), so feel free to forward this link to anyone who needs to calculate their own numbers.
        </p>

        <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="quote-items-table-container">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent bg-gray-900 border-b-0">
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold">Photo</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold min-w-[220px]">Description</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold">Item No</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold">Size</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold text-right">Pcs/CTN</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold text-right">QTY</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold text-right">Price</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold text-right">Carton Qty</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold text-right">Total Qty</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold text-right">Total Amount</TableHead>
                <TableHead className="text-white text-[11px] uppercase tracking-label font-semibold text-right">CBM</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {computedItems.map((item, idx) => {
                const isGroupStart = groupSize[idx] > 0;
                return (
                  <TableRow key={item.item_id} data-testid={`quote-item-row-${item.item_id}`} className="border-b border-gray-100">
                    {isGroupStart && (
                      <TableCell rowSpan={groupSize[idx]} className="align-top py-3">
                        {item.photo_url ? <img src={item.photo_url} alt={item.item_no} className="w-14 h-14 object-cover rounded-lg" /> : <div className="w-14 h-14 bg-gray-50 rounded-lg" />}
                      </TableCell>
                    )}
                    {isGroupStart && (
                      <TableCell rowSpan={groupSize[idx]} className="align-top py-3 text-sm text-gray-700">{item.description || "-"}</TableCell>
                    )}
                    <TableCell className="font-mono text-xs font-bold text-gray-900">{item.item_no}</TableCell>
                    <TableCell className="text-sm text-gray-500">{item.size || "-"}</TableCell>
                    <TableCell className="font-mono text-sm text-right text-gray-500">{item.pcs_per_ctn ?? "-"}</TableCell>
                    <TableCell className="font-mono text-sm text-right text-gray-500" data-testid={`quote-ci-inventory-qty-${item.item_id}`}>{item.ci_inventory_qty ?? "-"}</TableCell>
                    <TableCell className="font-mono text-sm text-right font-bold text-gray-900">{formatCurrency(item.final_price ?? item.price, currency)}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        min="0"
                        value={cartonQty[item.item_id] ?? 0}
                        onChange={(e) => setCartonQty((c) => ({ ...c, [item.item_id]: parseInt(e.target.value, 10) || 0 }))}
                        data-testid={`quote-carton-qty-input-${item.item_id}`}
                        className="w-20 h-9 rounded-lg border-gray-300 text-right font-mono text-sm ml-auto focus-visible:ring-brand-500"
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm text-right text-gray-500">{item.total_qty}</TableCell>
                    <TableCell className="font-mono text-sm text-right font-bold text-gray-900" data-testid={`quote-total-amount-${item.item_id}`}>{formatCurrency(item.total_amount, currency)}</TableCell>
                    <TableCell className="text-sm text-right text-gray-400">-</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-end items-baseline gap-6 pt-2">
          <span className="text-sm text-gray-500">Total CBM: <span className="font-semibold text-gray-900">{totalCbm.toFixed(3)} m³</span></span>
          <span className="text-lg text-gray-700">Grand Total: <span className="font-bold text-brand-600" data-testid="quote-grand-total">{formatCurrency(grandTotal, currency)}</span></span>
        </div>
      </div>
    </div>
  );
}
